import React, { useState } from "react";
import {
  Plus,
  FolderPlus,
  Folder as FolderIcon,
  Music,
  Sword,
  Footprints,
  Trees,
  Wind,
  Waves,
  Zap,
  Shield,
  Crown,
  ChevronRight,
  Home,
} from "lucide-react";
import { ProjectSelector, type Project } from "./ProjectSelector";
import { CreateProjectDialog } from "./CreateProjectDialog";
import { IconSelector } from "./IconSelector";
import type { User } from "@supabase/supabase-js";
import type { Folder } from "../types";
import { Button } from "@/components/ui/button";

interface SidebarProps {
  projects: Project[];
  projectsLoading?: boolean;
  selectedProjectId: string;
  onSelectProject: (projectId: string) => void;
  onCreateProject: (projectName: string) => void;
  folders: Folder[];
  foldersLoading?: boolean;
  selectedFolderId: string | null;
  onSelectFolder: (folderId: string | null) => void;
  onCreateFolder: (folderName: string, icon: string) => void;
  onUpdateFolderIcon?: (folderId: string, icon: string) => void;
  user: User | null;
}

// Icon mapping for folder display
const iconComponents = {
  folder: FolderIcon,
  music: Music,
  sword: Sword,
  footprints: Footprints,
  trees: Trees,
  wind: Wind,
  waves: Waves,
  zap: Zap,
  shield: Shield,
  crown: Crown,
};

export function Sidebar({
  projects,
  projectsLoading: _projectsLoading,
  selectedProjectId,
  onSelectProject,
  onCreateProject,
  folders,
  foldersLoading,
  selectedFolderId,
  onSelectFolder,
  onCreateFolder,
  onUpdateFolderIcon,
  user,
}: SidebarProps) {
  const [isCreateProjectOpen, setIsCreateProjectOpen] = useState(false);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [iconSelectorPosition, setIconSelectorPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [pendingFolderName, setPendingFolderName] = useState("");
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);

  const handleCreateFolder = () => {
    if (!selectedProjectId) return;
    setIsCreatingFolder(true);
    setNewFolderName("");
  };

  const handleIconSelect = (iconName: string) => {
    if (editingFolderId) {
      onUpdateFolderIcon?.(editingFolderId, iconName);
      setEditingFolderId(null);
    } else if (pendingFolderName) {
      onCreateFolder(pendingFolderName, iconName);
      setNewFolderName("");
      setPendingFolderName("");
      setIsCreatingFolder(false);
    }
    setIconSelectorPosition(null);
  };

  const handleCancelFolder = () => {
    setIsCreatingFolder(false);
    setNewFolderName("");
    setPendingFolderName("");
    setEditingFolderId(null);
    setIconSelectorPosition(null);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      handleCancelFolder();
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (newFolderName.trim()) {
        setPendingFolderName(newFolderName.trim());
        showIconSelectorBelowInput(e.currentTarget as HTMLInputElement);
      }
    }
  };

  const showIconSelectorBelowInput = (inputElement: HTMLInputElement) => {
    const rect = inputElement.getBoundingClientRect();
    setIconSelectorPosition({
      x: rect.left,
      y: rect.bottom + 5,
    });
  };

  const handleEditFolderIcon = (folderId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const buttonRect = (
      event.currentTarget as HTMLButtonElement
    ).getBoundingClientRect();
    setEditingFolderId(folderId);
    setIconSelectorPosition({
      x: buttonRect.left - 10,
      y: buttonRect.bottom + 5,
    });
  };

  return (
    <div className="w-72 bg-background border-r border-border flex flex-col h-full">
      {/* Project Selector */}
      <div className="px-6 py-6 border-b border-border">
        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Project
            </label>
          </div>
          <ProjectSelector
            projects={projects}
            selectedProjectId={selectedProjectId}
            onSelectProject={onSelectProject}
          />
          <Button
            onClick={() => setIsCreateProjectOpen(true)}
            variant="outline"
            className="w-full justify-start"
            disabled={!user}
          >
            <Plus size={16} className="mr-2" />
            <span>New Project</span>
          </Button>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-6">
        <div className="px-6 space-y-2">
          {/* All Sounds */}
          <button
            onClick={() => onSelectFolder(null)}
            className={`
              w-full flex items-center space-x-3 px-3 py-2 rounded-md text-sm
              transition-colors duration-200 group
              ${
                !selectedFolderId
                  ? "bg-accent text-accent-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
              }
            `}
          >
            <Home size={16} />
            <span>All Sounds</span>
            <div className="ml-auto">
              <ChevronRight
                size={12}
                className={`
                transition-all duration-200
                ${!selectedFolderId ? "opacity-100 rotate-90" : "opacity-0 group-hover:opacity-50"}
              `}
              />
            </div>
          </button>

          {/* Folders Section */}
          {selectedProjectId && (
            <>
              <div className="flex items-center justify-between pt-4 pb-2">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Folders
                </span>
                <Button
                  onClick={handleCreateFolder}
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  disabled={!user}
                >
                  <FolderPlus size={14} />
                </Button>
              </div>

              {foldersLoading ? (
                <div className="py-2">
                  <div className="flex items-center space-x-2 text-muted-foreground">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent" />
                    <span className="text-sm">Loading folders...</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  {folders.map((folder) => {
                    const IconComponent =
                      iconComponents[
                        folder.icon as keyof typeof iconComponents
                      ] || FolderIcon;
                    return (
                      <button
                        key={folder.id}
                        onClick={() => onSelectFolder(folder.id)}
                        className={`
                          w-full flex items-center space-x-3 px-3 py-2 rounded-md text-sm
                          transition-colors duration-200 group
                          ${
                            selectedFolderId === folder.id
                              ? "bg-accent text-accent-foreground font-medium"
                              : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                          }
                        `}
                      >
                        <div
                          className="cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={(e) => handleEditFolderIcon(folder.id, e)}
                        >
                          <IconComponent size={16} />
                        </div>
                        <span className="flex-1 text-left">{folder.name}</span>
                        <div className="ml-auto">
                          <ChevronRight
                            size={12}
                            className={`
                            transition-all duration-200
                            ${selectedFolderId === folder.id ? "opacity-100 rotate-90" : "opacity-0 group-hover:opacity-50"}
                          `}
                          />
                        </div>
                      </button>
                    );
                  })}

                  {/* New Folder Input */}
                  {isCreatingFolder && (
                    <div className="py-2">
                      <input
                        type="text"
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        onKeyDown={handleInputKeyDown}
                        onBlur={() => {
                          if (!pendingFolderName) {
                            handleCancelFolder();
                          }
                        }}
                        placeholder="Folder name..."
                        className="w-full px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                        autoFocus
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Dialogs */}
      <CreateProjectDialog
        isOpen={isCreateProjectOpen}
        onClose={() => setIsCreateProjectOpen(false)}
        onCreateProject={(name) => {
          onCreateProject(name);
          setIsCreateProjectOpen(false);
        }}
      />

      {/* Icon Selector Popover */}
      {iconSelectorPosition && (
        <IconSelector
          position={iconSelectorPosition}
          onSelect={handleIconSelect}
          onClose={() => {
            setIconSelectorPosition(null);
            handleCancelFolder();
          }}
        />
      )}
    </div>
  );
}
